import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useFinancesAnalyticsByMonthsApi } from '@/features/finances/hooks/useFinancesAnalyticsByMonthsApi';
import { useRentAnalyticsExpenseVisibilityApi } from '@/features/finances/hooks/useRentAnalyticsExpenseVisibilityApi';
import {
    areAllRentAnalyticsExpenseLinesHidden,
    buildRentAnalyticsExpenseChartData,
} from '@/features/finances/constants/rentAnalyticsExpenseLines';
import ETooltip from '@/components/everridge/ETooltip';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from '@/components/ui/button';
import AnalyticsSummary from './AnalyticsSummary';
import AnalyticsExpenses from './AnalyticsExpenses';
import AnalyticsIncome from './AnalyticsIncome';
import { Skeleton } from '@/components/ui/skeleton';
import AnalyticsDetailsChartSVG from './AnalyticsDetailsChartSVG';
import AnalyticsDetailsWaterfallChart from './AnalyticsDetailsWaterfallChart';

export enum ActivityButtonType {
    Summary,
    Expenses,
    Income,
}

type Props = {
    className?: string;
    isMobile?: boolean;
};

const FinancesPageAnalytics: FC<Props> = ({ className, isMobile }) => {
    const [activity, setActivity] = useState<ActivityButtonType>(ActivityButtonType.Summary);
    const { t } = useTranslation();

    const { analyticsByMonthsData, isLoadingAnalyticsByMonthsData } =
        useFinancesAnalyticsByMonthsApi();

    const { hiddenExpenseKeySet } = useRentAnalyticsExpenseVisibilityApi();

    const allLinesHidden = useMemo(
        () => areAllRentAnalyticsExpenseLinesHidden(hiddenExpenseKeySet),
        [hiddenExpenseKeySet],
    );

    const expensesChartData = useMemo(
        () =>
            buildRentAnalyticsExpenseChartData(analyticsByMonthsData, hiddenExpenseKeySet),
        [analyticsByMonthsData, hiddenExpenseKeySet],
    );

    const activityButtonsList = useMemo(() => {
        const waterfallData: {
            value: number;
            color: string;
            label: string;
        }[] = [
            {
                value: analyticsByMonthsData?.grossRevenue ?? 0,
                color: '#2eb566',
                label: t(
                    'main.finances.components.analytics.components.summary.grossRevenue',
                ),
            },
        ];
        if (!allLinesHidden) {
            waterfallData.push({
                value: analyticsByMonthsData?.expenses ?? 0,
                color: '#e0351c',
                label: t('main.finances.components.analytics.components.summary.expenses'),
            });
        }
        waterfallData.push(
            {
                value: analyticsByMonthsData?.deduction ?? 0,
                color: '#565dcf',
                label: t('main.finances.components.analytics.components.summary.deduction'),
            },
            {
                value: analyticsByMonthsData?.transferredFunds ?? 0,
                color: '#e6a82a',
                label: t(
                    'main.finances.components.analytics.components.summary.transferredFunds',
                ),
            },
        );

        return [
            {
                type: ActivityButtonType.Summary,
                name: t('main.finances.components.analytics.components.summary.title'),
                chart: () => (
                    <AnalyticsDetailsWaterfallChart
                        data={waterfallData}
                        isLoading={isLoadingAnalyticsByMonthsData}
                    />
                ),
                data: () => (
                    <AnalyticsSummary
                        data={analyticsByMonthsData}
                        isLoading={isLoadingAnalyticsByMonthsData}
                        showExpensesSummary={!allLinesHidden}
                    />
                ),
            },
            {
                type: ActivityButtonType.Expenses,
                name: t('main.finances.components.analytics.components.expenses.title'),
                chart: () => (
                    <AnalyticsDetailsChartSVG
                        label={'main.finances.components.analytics.components.expenses.chartTitle'}
                        data={expensesChartData}
                        isLoading={isLoadingAnalyticsByMonthsData}
                    />
                ),
                data: () => (
                    <AnalyticsExpenses
                        data={analyticsByMonthsData}
                        isLoading={isLoadingAnalyticsByMonthsData}
                        hiddenExpenseKeySet={hiddenExpenseKeySet}
                    />
                ),
            },
            {
                type: ActivityButtonType.Income,
                name: t('main.finances.components.analytics.components.income.title'),
                chart: () => (
                    <AnalyticsDetailsChartSVG
                        label={'main.finances.components.analytics.components.income.chartTitle'}
                        data={[
                            {
                                value: analyticsByMonthsData?.income ?? 0,
                                color: '#2EB566',
                            },
                        ]}
                        isLoading={isLoadingAnalyticsByMonthsData}
                    />
                ),
                data: () => (
                    <AnalyticsIncome
                        data={analyticsByMonthsData}
                        isLoading={isLoadingAnalyticsByMonthsData}
                    />
                ),
            },
        ];
    }, [
        t,
        analyticsByMonthsData,
        isLoadingAnalyticsByMonthsData,
        allLinesHidden,
        expensesChartData,
        hiddenExpenseKeySet,
    ]);

    const visibleActivityButtonsList = useMemo(
        () =>
            activityButtonsList.filter(
                (item) => item.type !== ActivityButtonType.Expenses || !allLinesHidden,
            ),
        [activityButtonsList, allLinesHidden],
    );

    useEffect(() => {
        if (allLinesHidden && activity === ActivityButtonType.Expenses) {
            setActivity(ActivityButtonType.Summary);
        }
    }, [allLinesHidden, activity]);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'center',
        dragFree: true,
    });

    const clickHandler = (type: ActivityButtonType) => {
        setActivity(type);
        if (emblaApi) {
            const idx = visibleActivityButtonsList.findIndex((x) => x.type === type);
            if (idx >= 0) emblaApi.scrollTo(idx);
        }
    };

    const displayItem =
        visibleActivityButtonsList.find((element) => element.type === activity) ??
        visibleActivityButtonsList[0];

    const tabSkeletonCount = visibleActivityButtonsList.length;

    return (
        <div
            className={cn(
                'w-full p-6 flex flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10',
                isMobile && 'h-fit p-0 border-0 bg-popover-30 gap-4',
                className,
            )}
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <h4 className={cn('text-xl font-medium', isMobile && 'text-lg')}>
                        {t('main.finances.components.analytics.rentAnalytics')}
                    </h4>
                    <ETooltip content={t('main.finances.components.analytics.tooltip')} />
                </div>
            </div>
            <div className={cn('p-6 flex flex-col gap-6', isMobile && 'p-0')}>
                <div className="w-full flex items-center justify-center">{displayItem?.chart()}</div>
                <div
                    className={cn('-mx-6 px-6 overflow-hidden', isMobile && '-mx-4 px-4')}
                    ref={emblaRef}
                >
                    <div className="flex justify-center gap-2 touch-pan-y">
                        {isLoadingAnalyticsByMonthsData
                            ? Array.from({ length: tabSkeletonCount }).map((_, index) => (
                                  <Skeleton key={index} className="h-9 w-22.5 rounded-full" />
                              ))
                            : visibleActivityButtonsList.map(({ type: tabType, name }) => (
                                  <Button
                                      key={tabType}
                                      variant={activity === tabType ? 'default' : 'outline'}
                                      className={cn(
                                          `h-9 px-3.5 py-2 hover:shadow-none duration-300 ${
                                              activity === tabType
                                                  ? ''
                                                  : 'hover:bg-primary-color-50'
                                          } `,
                                      )}
                                      onClick={() => clickHandler(tabType)}
                                  >
                                      {name}
                                  </Button>
                              ))}
                    </div>
                </div>
                <div className="flex-1">{displayItem?.data()}</div>
            </div>
        </div>
    );
};

export default FinancesPageAnalytics;
